import { computed, effect, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { selectCurrentCountry } from '@stores/router.store';
import { BankingPartnerWebservice } from '@wizbii-utils/angular/webservices';
import { BankingPartnerDetails, LangEnum } from '@wizbii/utils/models';

type BankingPartnerState = {
  bankingPartnerList: BankingPartnerDetails[] | undefined;
  isLoading: boolean;
  country: LangEnum | undefined;
};

const initialState: BankingPartnerState = {
  bankingPartnerList: undefined,
  isLoading: false,
  country: undefined,
};

export const BankingPartnerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    enabledBankingPartnerList: computed(() => {
      const list = store.bankingPartnerList() ?? [];
      return list.filter((partner) => partner.isEnabled);
    }),
    partnerIdLabelMapping: computed(() => {
      const list = store.bankingPartnerList() ?? [];
      return list.reduce(
        (acc, current) => {
          acc[current.id] = current.label;
          return acc;
        },
        {} as Record<string, string>
      );
    }),
  })),

  withMethods((store, bankingPartnerWebservice = inject(BankingPartnerWebservice)) => ({
    loadBankingPartnerList: (country: LangEnum) => {
      patchState(store, { isLoading: true });
      return bankingPartnerWebservice.getAllCompletePartners(country).subscribe({
        next: (res) => {
          if (store.country() === country) {
            patchState(store, { bankingPartnerList: res, isLoading: false });
          }
        },
        error: () => {
          patchState(store, { isLoading: false });
        },
      });
    },
    partnerFromId: (id: string): BankingPartnerDetails | undefined => {
      const list = store.bankingPartnerList();
      if (list) {
        return list.find((bankingPartner) => bankingPartner.id === id);
      }
      return undefined;
    },
  })),
  withHooks({
    onInit(store, countryStore = inject(Store)) {
      effect(() => {
        const country = countryStore.selectSignal(selectCurrentCountry)();
        if (country != store.country()) {
          patchState(store, { country: country });
          store.loadBankingPartnerList(country);
        }
      });
    },
  })
);
